import { useCallback, useEffect, useState } from 'react';
import { DEFAULT_NEW_WAGER } from '@bo-constants/newWagers';
import type { FormattedNewWager } from '@library/types/newWagers';
import { useGetNewWagerByIdQuery } from '@bo-services/members/newWagersApi';
import { useSearchParams } from 'react-router-dom';

export interface MemberEntryModalProps {
  memberId?: string;
}

export const useMemberEntryModal = ({ memberId }: MemberEntryModalProps) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedNewWager, setSelectedNewWager] =
    useState<FormattedNewWager>(DEFAULT_NEW_WAGER);

  const [searchParams, setSearchParams] = useSearchParams();
  const entryId = searchParams.get('entryId') ?? undefined;
  const newWagerId = selectedNewWager?.newWager?.id || entryId;

  const { data: newWagerData } = useGetNewWagerByIdQuery(
    {
      id: newWagerId,
      userId: memberId
    },
    {
      skip: !newWagerId || !memberId
    }
  );

  const handleShowModal = useCallback((newWager: FormattedNewWager) => {
    setSelectedNewWager(newWager);
    setShowModal(true);
  }, []);

  const handleCancelModal = useCallback(() => {
    setSearchParams();
    setShowModal(false);
    setSelectedNewWager(DEFAULT_NEW_WAGER);
  }, [setSearchParams]);

  useEffect(() => {
    if (newWagerData?.data?.length) {
      const newWager = newWagerData.data[0];
      setSelectedNewWager(newWager);

      if (entryId && searchParams.size) {
        handleShowModal(newWager);
      }
    }
  }, [entryId, handleShowModal, newWagerData, searchParams]);

  return {
    onCancelModal: handleCancelModal,
    onShowModal: handleShowModal,
    selectedNewWager,
    showModal
  };
};
